import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agentProfitRangeList, agentProfitRangeAdd, agentProfitRangeUpdate, agentProfitRangeRemove } from "../../Redux/Actions/agentProfitRangeActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { columnsAgentProfitRangeList } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import AgentProfitRangeEditModal from "./addAgentProfitRange";
import { Button, Modal } from "antd";

const ListAgentProfitRange = () => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProfitRange, setSelectedProfitRange] = useState(null);
    const [tempSearch, setTempSearch] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
    const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');


    const fetchDetails = useCallback(
        (showLoader = true) => {
            const payload = {
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchQuery,
            };
            return dispatch(agentProfitRangeList(payload, showLoader)).catch(() => {
                if (showLoader) {
                    toast.error("Failed to fetch agent profit range list");
                }
            });
        },
        [dispatch, pagination.current, pagination.pageSize, searchQuery]
    );

    const agentProfitRangeState = useSelector((state) => state.agentProfitRange || {});
    const agentProfitRanges = useMemo(() => {
        return agentProfitRangeState.agentProfitRanges || [];
    }, [agentProfitRangeState.agentProfitRanges]);
    const agentProfitRangeTotal = useMemo(() => {
        return agentProfitRangeState.totalCount || 0;
    }, [agentProfitRangeState.totalCount]);
    const { loading } = agentProfitRangeState;

    useEffect(() => {
        dispatch(getEnums());
    }, [dispatch]);

    const enumsData = useSelector((state) => state.enumsData);

    useEffect(() => {
        fetchDetails(true);
    }, [fetchDetails]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, total: agentProfitRangeTotal }));
    }, [agentProfitRangeTotal]);

    const openEditModal = useCallback((profitRange = null) => {
        setSelectedProfitRange(profitRange);
        setIsModalOpen(true);
    }, []);

    const closeEditModal = () => {
        setSelectedProfitRange(null);
        setIsModalOpen(false);
    };

    const filterHandler = () => {
        setSearchQuery(tempSearch);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleDelete = (profitRangeId) => {
        Modal.confirm({
            title: "Confirm Delete",
            content: "Are you sure you want to delete this agent profit range?",
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    await dispatch(agentProfitRangeRemove(profitRangeId));
                    fetchDetails(false);
                } catch (error) {
                    toast.error("Failed to delete agent profit range");
                }
            },
        });
    };

    const handleSubmit = async (values) => {
        const prevProfitRanges = agentProfitRanges;

        // Check for duplicate price range
        const isDuplicateRange = prevProfitRanges.some(
            (range) =>
                range.min_price === values.min_price &&
                range.max_price === values.max_price &&
                range._id !== selectedProfitRange?._id
        );

        if (isDuplicateRange) {
            toast.error("Price range already exists");
            return { success: false };
        }

        try {
            let result;
            if (selectedProfitRange?._id) {
                result = await dispatch(agentProfitRangeUpdate({
                    id: selectedProfitRange._id,
                    ...values
                }));
            } else {
                result = await dispatch(agentProfitRangeAdd(values));
            }

            if (result) {
                fetchDetails(false);
                closeEditModal();
                return { success: true };
            }
        } catch (error) {
            toast.error("Failed to save agent profit range");
            return { success: false };
        }
    };

    const handleTableChange = (pagination) => {
        setPagination(pagination);
        fetchDetails(false);
    };

    return (
        <>
            <div style={{ position: "absolute", marginTop: "9.6%", marginLeft: "88.5%" }}>
                {canAdd &&
                    <Button
                        style={{ background: "#0D1A52", color: "white", padding: "18px", borderRadius: "20px" }}
                        onClick={() => openEditModal(null)}
                    >
                        Add Range
                    </Button>
                }
            </div>

            <TableComponent
                pagination={pagination}
                filterFunction={filterHandler}
                onPressEnter={filterHandler}
                setState={setTempSearch}
                dataSource={agentProfitRanges}
                onChange={handleTableChange}
                h3heading="Agent Range List"
                columns={columnsAgentProfitRangeList(pagination.current, pagination.pageSize, handleDelete, openEditModal, canEdit)}
                loading={loading}
            />

            {isModalOpen && (
                <AgentProfitRangeEditModal
                    visible={isModalOpen}
                    profitRangeData={selectedProfitRange}
                    onSave={handleSubmit}
                    onClose={closeEditModal}
                    enumsData={enumsData}
                />
            )}
        </>
    );
};

export default ListAgentProfitRange;
